import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/contexts/theme-provider";
import { Navigate, Route, Routes } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import "./App.css";
import { Layout } from "./Layout";
import { RouteLegacyNavigationOnly } from "./components/route-legacy-navigation-only";
import { RouteProtected } from "./components/route-protected";
import { Toaster } from "./components/ui/toaster";
import { AdminModeProvider } from "./contexts/admin-mode-provider";
import { ChatStreamProvider } from "./contexts/chat-stream-context";
import { CreditsProvider } from "./contexts/credits-provider";
import { GoogleCalendarIntegrationProvider } from "./contexts/google-calendar-integration-provider";
import { HubSpotIntegrationProvider } from "./contexts/hubspot-integration-provider";
import { SeliarisIntegrationProvider } from "./contexts/seliaris-integration-provider";
import { CreateListingDialogProvider } from "./contexts/create-listing-dialog-provider";
import { ListingsProvider } from "./contexts/listings-provider";
import { OutlookCalendarIntegrationProvider } from "./contexts/outlook-calendar-integration-provider";
import { PricesProvider } from "./contexts/prices-provider";
import { TeamsProvider } from "./contexts/teams-provider";
import { UserProvider } from "./contexts/user-provider";
import { UsersProvider } from "./contexts/users-provider";
import { CheckoutCancelledPage } from "./pages/checkout-cancelled";
import { CheckoutSuccessPage } from "./pages/checkout-success";
import AdministrationPage from "./pages/desktop/administration";
import AdministrationGodBoardPage from "./pages/desktop/administration/god-board";
import AdministrationTeamsPage from "./pages/desktop/administration/teams";
import AdministrationUsersPage from "./pages/desktop/administration/users";
import AdministrationUsersHistoryPage from "./pages/desktop/administration/users-history";
import ChatPage from "./pages/desktop/chat";
import CompanyPage from "./pages/desktop/company";
import CompanyEmployeesPage from "./pages/desktop/company/employees";
import CompanyFinancialsPage from "./pages/desktop/company/financials";
import CompanyInsightsPage from "./pages/desktop/company/insights";
import CompanyAdministrativePage from "./pages/desktop/company/administrative";
import CompanyNewsPage from "./pages/desktop/company/news";
import CompanyOthersPage from "./pages/desktop/company/others";
import CompanySimilarsPage from "./pages/desktop/company/similars";
import IntegrationApiKeysPage from "./pages/desktop/integrations/api";
import IntegrationChromeExtensionPage from "./pages/desktop/integrations/chrome-extension";
import IntegrationEdgeExtensionPage from "./pages/desktop/integrations/edge-extension";
import IntegrationGoogleCalendarPage from "./pages/desktop/integrations/google-calendar";
import IntegrationHubspotPage from "./pages/desktop/integrations/hubspot";
import IntegrationSeliarisPage from "./pages/desktop/integrations/seliaris";
import IntegrationMainPage from "./pages/desktop/integrations/index";
import IntegrationsPage from "./pages/desktop/integrations/layout";
import IntegrationOutlookCalendarPage from "./pages/desktop/integrations/outlook-calendar";
import ListingPage from "./pages/desktop/listing";
import ListingAssignationsCompaniesPage from "./pages/desktop/listing/assignations-companies";
import ListingAssignationsEmployeesPage from "./pages/desktop/listing/assignations-employees";
import ListingContentPage from "./pages/desktop/listing/content";
import TeamMemberDetailPage from "./pages/desktop/listing/team-member-detail";
import ProspectingPage from "./pages/desktop/prospecting";
import HistoryPage from "./pages/desktop/prospecting/history";
import SearchPage from "./pages/desktop/prospecting/search";
import SettingsPage from "./pages/desktop/settings";
import SettingsInstructionPage from "./pages/desktop/settings/instruction";
import SettingsLanguagePage from "./pages/desktop/settings/language";
import TeamPage from "./pages/desktop/team";
import TeamActivityPage from "./pages/desktop/team/activity";
import TeamBillingPage from "./pages/desktop/team/billing";
import TeamCreditsPage from "./pages/desktop/team/credits";
import TeamKnowledgePage from "./pages/desktop/team/knowledge-base";
import TeamSettingsPage from "./pages/desktop/team/settings";
import TeamUsersPage from "./pages/desktop/team/users";
import NotFoundPage from "./pages/shared/not-found";
import ReferralsPage from "./pages/shared/referrals";
import { SetupTeamCreationPage } from "./pages/shared/setup/team-creation";
import { SetupTeamCreationBilling } from "./pages/shared/setup/team-creation-billing";
import { SetupPaymentFailedPage } from "./pages/shared/setup/payment-failed";
import { SetupPaymentFailedBillingPage } from "./pages/shared/setup/payment-failed-billing";
import { SetupResubscriptionPage } from "./pages/shared/setup/resubscription";
import { SetupResubscriptionBilling } from "./pages/shared/setup/resubscription-billing";
import { SetupTrialEndedPage } from "./pages/shared/setup/trial-ended";
import { SetupTrialEndedBilling } from "./pages/shared/setup/trial-ended-billing";

export function AppDesktop({ home }: { home: React.ReactNode }) {
  return (
    <>
      <ThemeProvider>
        <AdminModeProvider>
          <TooltipProvider>
              <TeamsProvider>
                <UserProvider>
                  <UsersProvider>
                    <CreditsProvider>
                      <PricesProvider>
                        <ListingsProvider>
                          <CreateListingDialogProvider>
                          <HubSpotIntegrationProvider>
                          <SeliarisIntegrationProvider>
                            <GoogleCalendarIntegrationProvider>
                              <OutlookCalendarIntegrationProvider>
                                <ChatStreamProvider>
                                  <QueryParamProvider
                                    adapter={ReactRouter6Adapter}
                                  >
                                    <Routes>
                                      <Route path="/">
                                        <Route index path="/" element={home} />
                                        <Route
                                          path="checkout-success"
                                          element={<CheckoutSuccessPage />}
                                        />
                                        <Route
                                          path="checkout-cancelled"
                                          element={<CheckoutCancelledPage />}
                                        />
                                        <Route
                                          path="setup"
                                          element={
                                            <Layout
                                              showNavMain={false}
                                              showNavSearch={false}
                                              navLinkToTheRoot={false}
                                            />
                                          }
                                        >
                                          <Route
                                            index
                                            element={
                                              <Navigate to="/" replace />
                                            }
                                          />
                                          <Route
                                            path="team-creation"
                                            element={<SetupTeamCreationPage />}
                                          />
                                          <Route
                                            path="team-creation-billing"
                                            element={
                                              <RouteProtected
                                                requiredRoles={[
                                                  "billing",
                                                  "manager",
                                                  "admin",
                                                  "advisor",
                                                ]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupTeamCreationBilling />
                                                }
                                              />
                                            }
                                          />
                                          <Route
                                            path="trial-ended"
                                            element={
                                              <RouteProtected
                                                requiredRoles={[
                                                  "admin",
                                                  "advisor",
                                                  "manager",
                                                ]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupTrialEndedPage />
                                                }
                                              />
                                            }
                                          />
                                          <Route
                                            path="trial-ended-billing"
                                            element={
                                              <RouteProtected
                                                requiredRoles={["billing"]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupTrialEndedBilling />
                                                }
                                              />
                                            }
                                          />
                                          <Route
                                            path="payment-failed"
                                            element={
                                              <RouteProtected
                                                requiredRoles={[
                                                  "admin",
                                                  "advisor",
                                                  "manager",
                                                ]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupPaymentFailedPage />
                                                }
                                              />
                                            }
                                          />
                                          <Route
                                            path="payment-failed-billing"
                                            element={
                                              <RouteProtected
                                                requiredRoles={["billing"]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupPaymentFailedBillingPage />
                                                }
                                              />
                                            }
                                          />
                                          <Route
                                            path="resubscription"
                                            element={
                                              <RouteProtected
                                                requiredRoles={[
                                                  "admin",
                                                  "advisor",
                                                  "manager",
                                                ]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupResubscriptionPage />
                                                }
                                              />
                                            }
                                          />
                                          <Route
                                            path="resubscription-billing"
                                            element={
                                              <RouteProtected
                                                requiredRoles={["billing"]}
                                                licenceActiveState={false}
                                                element={
                                                  <SetupResubscriptionBilling />
                                                }
                                              />
                                            }
                                          />
                                        </Route>
                                        <Route element={<Layout />}>
                                          <Route
                                            path="chat"
                                            element={
                                              <RouteProtected
                                                requiredLicences={[
                                                  "basic",
                                                  "professional",
                                                ]}
                                                licenceActiveState={true}
                                                element={<ChatPage />}
                                              />
                                            }
                                          >
                                            <Route index />
                                            <Route path=":conversationId" />
                                          </Route>
                                          {/* The guard sits outside RouteProtected: a
                                              switched-over team must reach the chat
                                              before any licence verdict, which would
                                              otherwise bounce it to "/". */}
                                          <Route
                                            path="prospecting"
                                            element={
                                              <RouteLegacyNavigationOnly
                                                element={
                                                  <RouteProtected
                                                    requiredLicences={[
                                                      "professional",
                                                    ]}
                                                    licenceActiveState={true}
                                                    element={
                                                      <ProspectingPage />
                                                    }
                                                  />
                                                }
                                              />
                                            }
                                          >
                                            <Route
                                              index
                                              path="search"
                                              element={<SearchPage />}
                                            />
                                            <Route
                                              path="search/:searchId"
                                              element={<SearchPage />}
                                            />
                                            <Route
                                              path="history"
                                              element={<HistoryPage />}
                                            />
                                          </Route>
                                          <Route
                                            path="listing"
                                            element={
                                              <RouteProtected
                                                requiredLicences={[
                                                  "professional",
                                                ]}
                                                licenceActiveState={true}
                                                element={<ListingPage />}
                                              />
                                            }
                                          >
                                            <Route index />
                                            <Route
                                              path="assignations"
                                              element={
                                                <Navigate
                                                  to="/listing/assignations/companies"
                                                  replace
                                                />
                                              }
                                            />
                                            <Route
                                              path="assignations/companies"
                                              element={
                                                <ListingAssignationsCompaniesPage />
                                              }
                                            />
                                            <Route
                                              path="assignations/employees"
                                              element={
                                                <ListingAssignationsEmployeesPage />
                                              }
                                            />
                                            <Route
                                              path="team-members/:userId"
                                              element={<TeamMemberDetailPage />}
                                            />
                                            <Route
                                              path=":listingId"
                                              element={<ListingContentPage />}
                                            />
                                          </Route>
                                          <Route
                                            path="team"
                                            element={
                                              <RouteProtected
                                                requiredRoles={[
                                                  "admin",
                                                  "advisor",
                                                  "manager",
                                                ]}
                                                licenceActiveState={true}
                                                element={<TeamPage />}
                                              />
                                            }
                                          >
                                            <Route
                                              index
                                              path="activity"
                                              element={<TeamActivityPage />}
                                            />
                                            <Route
                                              path="knowledge-base"
                                              element={<TeamKnowledgePage />}
                                            />
                                            <Route
                                              path="settings"
                                              element={<TeamSettingsPage />}
                                            />
                                            <Route
                                              path="billing"
                                              element={<TeamBillingPage />}
                                            />
                                            <Route
                                              path="users"
                                              element={<TeamUsersPage />}
                                            />
                                            <Route
                                              path="credits"
                                              element={<TeamCreditsPage />}
                                            />
                                          </Route>
                                          <Route
                                            path="integrations"
                                            element={<IntegrationsPage />}
                                          >
                                            <Route
                                              index
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationMainPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="api"
                                              element={
                                                <RouteProtected
                                                  requiredRoles={[
                                                    "admin",
                                                    "manager",
                                                  ]}
                                                  element={
                                                    <IntegrationApiKeysPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="hubspot"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationHubspotPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="seliaris"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationSeliarisPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="google-calendar"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationGoogleCalendarPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="outlook-calendar"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationOutlookCalendarPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="chrome-extension"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationChromeExtensionPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="edge-extension"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <IntegrationEdgeExtensionPage />
                                                  }
                                                />
                                              }
                                            />
                                          </Route>
                                          <Route
                                            path="settings"
                                            element={<SettingsPage />}
                                          >
                                            <Route
                                              index
                                              path="instruction"
                                              element={
                                                <RouteProtected
                                                  licenceActiveState={true}
                                                  element={
                                                    <SettingsInstructionPage />
                                                  }
                                                />
                                              }
                                            />
                                            <Route
                                              path="language"
                                              element={<SettingsLanguagePage />}
                                            />
                                          </Route>
                                          <Route
                                            path="referrals"
                                            element={<ReferralsPage />}
                                          />
                                          <Route
                                            path="company/:companyId"
                                            element={
                                              <RouteProtected
                                                requiredLicences={[
                                                  "professional",
                                                ]}
                                                licenceActiveState={true}
                                                element={<CompanyPage />}
                                              />
                                            }
                                          >
                                            <Route
                                              index
                                              path="insights"
                                              element={<CompanyInsightsPage />}
                                            />
                                            <Route
                                              path="employees"
                                              element={<CompanyEmployeesPage />}
                                            />
                                            <Route
                                              path="news"
                                              element={<CompanyNewsPage />}
                                            />
                                            <Route
                                              path="financials"
                                              element={<CompanyFinancialsPage />}
                                            />
                                            <Route
                                              path="administrative"
                                              element={<CompanyAdministrativePage />}
                                            />
                                            <Route
                                              path="similars"
                                              element={<CompanySimilarsPage />}
                                            />
                                            <Route
                                              path="others"
                                              element={<CompanyOthersPage />}
                                            />
                                          </Route>
                                          <Route
                                            path="administration"
                                            element={
                                              <RouteProtected
                                                requiredRoles={["admin"]}
                                                element={<AdministrationPage />}
                                              />
                                            }
                                          >
                                            <Route
                                              index
                                              path="teams"
                                              element={
                                                <AdministrationTeamsPage />
                                              }
                                            />
                                            <Route
                                              path="users-history"
                                              element={
                                                <AdministrationUsersHistoryPage />
                                              }
                                            />
                                            <Route
                                              path="users"
                                              element={
                                                <AdministrationUsersPage />
                                              }
                                            />
                                            <Route
                                              path="god-board"
                                              element={
                                                <AdministrationGodBoardPage />
                                              }
                                            />
                                          </Route>
                                          <Route
                                            path="*"
                                            element={<NotFoundPage />}
                                          />
                                        </Route>
                                      </Route>
                                    </Routes>
                                  </QueryParamProvider>
                                  <Toaster />
                                </ChatStreamProvider>
                              </OutlookCalendarIntegrationProvider>
                            </GoogleCalendarIntegrationProvider>
                          </SeliarisIntegrationProvider>
                          </HubSpotIntegrationProvider>
                          </CreateListingDialogProvider>
                        </ListingsProvider>
                      </PricesProvider>
                    </CreditsProvider>
                  </UsersProvider>
                </UserProvider>
              </TeamsProvider>
          </TooltipProvider>
        </AdminModeProvider>
      </ThemeProvider>
    </>
  );
}
